import {
  Box,
  Burger,
  Container,
  Drawer,
  Group,
  Menu,
  NullLogo,
  Stack,
  Text,
  UnstyledButton,
} from "@repo/ui/core";
import { useDisclosure } from "@repo/ui/hooks";
import {
  IconBolt,
  IconBrandAndroid,
  IconChevronDown,
  IconCode,
  IconDeviceDesktopBolt,
  IconLayout,
  IconTestPipe,
  IconUsers,
} from "@repo/ui/icons";
import { Link, useLocation } from "react-router";
import { Cta } from "@/components/Cta";
import styles from "@styles/site.module.css";

const productLinks = [
  { to: "/platform", label: "Platform Overview", icon: IconLayout, desc: "Complete CI/CD for automotive" },
  { to: "/products/android-builds", label: "Android Builds", icon: IconBrandAndroid, desc: "Burst compute and build snapshots" },
  { to: "/products/emulators", label: "Web Emulators", icon: IconDeviceDesktopBolt, desc: "Cloud emulators in your browser" },
  { to: "/products/remote-dev", label: "Remote Development", icon: IconCode, desc: "Browser-based IDE via GitLab" },
  { to: "/products/testing", label: "Automated Testing", icon: IconTestPipe, desc: "Behavioral testing at scale" },
];

const solutionLinks = [
  { to: "/solutions/engineering-leaders", label: "Engineering Leaders", icon: IconBolt, desc: "Scale teams, cut costs" },
  { to: "/solutions/developers", label: "Developers", icon: IconUsers, desc: "The DX you deserve" },
];

function NavMenu({ label, links, width }: { label: string; links: typeof productLinks; width: number }) {
  const location = useLocation();
  const active = links.some((l) => location.pathname === l.to);
  return (
    <Menu trigger="hover" openDelay={60} closeDelay={150} width={width} position="bottom-start" offset={10} withinPortal>
      <Menu.Target>
        <UnstyledButton className={`${styles.navLink} ${active ? styles.navLinkActive : ""}`}>
          {label}
          <IconChevronDown size={13} stroke={1.5} style={{ opacity: 0.6 }} />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown className={styles.menuDropdown}>
        {links.map((link) => (
          <Menu.Item
            key={link.to}
            component={Link}
            to={link.to}
            className={styles.menuItem}
            leftSection={<link.icon size={18} stroke={1.5} color="var(--na-accent-solid)" />}
          >
            <Text size="sm" fw={550}>
              {link.label}
            </Text>
            <Text size="xs" c="dimmed">
              {link.desc}
            </Text>
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

function MobileLinks({ links, heading, close }: { links: typeof productLinks; heading: string; close: () => void }) {
  return (
    <>
      <div className={styles.footerHeading}>{heading}</div>
      {links.map((link) => (
        <UnstyledButton
          key={link.to}
          component={Link}
          to={link.to}
          onClick={close}
          p="sm"
          style={{ borderRadius: "var(--na-r-sm)" }}
        >
          <Group gap="sm" wrap="nowrap">
            <link.icon size={20} stroke={1.5} color="var(--na-accent-solid)" />
            <Text size="sm" fw={550}>
              {link.label}
            </Text>
          </Group>
        </UnstyledButton>
      ))}
    </>
  );
}

export default function Navigation() {
  const [opened, { toggle, close }] = useDisclosure(false);

  return (
    <Box
      component="header"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: "rgba(10, 10, 11, 0.8)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        borderBottom: "1px solid var(--na-border)",
      }}
    >
      <Container size="xl">
        <Group justify="space-between" h={64} wrap="nowrap">
          <UnstyledButton
            component={Link}
            to="/"
            onClick={close}
            aria-label="Null Autos home"
            style={{ display: "flex", alignItems: "center", height: 64, lineHeight: 0 }}
          >
            <NullLogo compact w={34} />
          </UnstyledButton>

          <Group gap={4} visibleFrom="md" wrap="nowrap">
            <NavMenu label="Products" links={productLinks} width={320} />
            <NavMenu label="Solutions" links={solutionLinks} width={280} />
            <Box ml="sm">
              <Cta to="/contact" size="sm">
                Book a Demo
              </Cta>
            </Box>
          </Group>

          <Burger opened={opened} onClick={toggle} hiddenFrom="md" color="white" aria-label="Toggle menu" />
        </Group>
      </Container>
      <Box className={styles.gradBar} />

      <Drawer
        opened={opened}
        onClose={close}
        size="100%"
        padding="lg"
        hiddenFrom="md"
        withCloseButton={true}
        closeButtonProps={{ "aria-label": "Close menu", size: "lg" }}
        styles={{
          header: { background: "var(--na-bg)" },
          body: { background: "var(--na-bg)", paddingTop: 24 },
          content: { background: "var(--na-bg)" },
        }}
      >
        <Stack gap="xs">
          <MobileLinks links={productLinks} heading="Products" close={close} />
          <Box mt="lg">
            <MobileLinks links={solutionLinks} heading="Solutions" close={close} />
          </Box>
          <Box mt="xl">
            <Cta to="/contact">Book a Demo</Cta>
          </Box>
        </Stack>
      </Drawer>
    </Box>
  );
}
